/**
 * Iter 226 · Nurse Mobile View · standalone page at /nurse-app
 * Shows a nurse's day visits, tap-to-start, GPS-tagged check-in,
 * vitals capture on completion.
 */
import React, { useEffect, useState } from "react";
import { Path, MapPin, CheckCircle, Play, User } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

// Compact standalone auth — nurse enters their id + a shared PIN (dev only).
// Production wires the existing staff-app OTP flow.
async function apiCall(path, opts = {}, adminToken = null) {
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (adminToken) headers["Authorization"] = `Bearer ${adminToken}`;
  const r = await fetch(`${API}/api${path}`, { ...opts, headers });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || r.statusText);
  return d;
}

export default function NurseMobileView() {
  const [nurseId, setNurseId] = useState(localStorage.getItem("nurse_id") || "");
  const [adminToken] = useState(localStorage.getItem("rescigo_token"));
  const [visits, setVisits] = useState([]);
  const [today] = useState(new Date().toISOString().slice(0,10));

  const load = async () => {
    if (!nurseId || !adminToken) return;
    try {
      const d = await apiCall(`/health-services/nurses/visits?nurse_id=${nurseId}&date_from=${today}`, {}, adminToken);
      setVisits(d.visits || []);
    } catch (e) { console.warn(e); }
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [nurseId, adminToken]);

  const save = () => { localStorage.setItem("nurse_id", nurseId); load(); };

  if (!adminToken) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] p-6 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg text-center max-w-sm">
          <div className="font-heading text-lg mb-2">Nurse app</div>
          <div className="text-sm text-[#5C5A55]">Please sign in to the admin console first, then open this page in the same session.</div>
        </div>
      </div>
    );
  }

  if (!nurseId) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] p-6 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg max-w-sm w-full">
          <div className="font-heading text-lg mb-3">Nurse ID</div>
          <input placeholder="Enter nurse id (e.g. N-T1)" data-testid="nurse-id-input"
                  value={nurseId} onChange={e => setNurseId(e.target.value)}
                  className="w-full border rounded px-3 py-2 mb-3"/>
          <button onClick={save} data-testid="nurse-id-save"
                  className="w-full py-2 rounded bg-[#B91C1C] text-white font-medium">Load my day</button>
        </div>
      </div>
    );
  }

  const planned = visits.filter(v => v.status === "planned");
  const inProgress = visits.filter(v => v.status === "in_progress");
  const done = visits.filter(v => v.status === "completed");

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="nurse-mobile-root">
      <header className="bg-white border-b border-[#E5E2D9] px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#8A8780]">Today · {today}</div>
            <div className="font-heading text-lg">Nurse {nurseId}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] text-[#8A8780]">Planned · In-progress · Done</div>
            <div className="font-mono text-sm">{planned.length} · {inProgress.length} · {done.length}</div>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-3 max-w-2xl mx-auto">
        {visits.length === 0 && (
          <div className="p-6 bg-white rounded-lg text-center text-[#8A8780]">No visits assigned today</div>
        )}
        {[...inProgress, ...planned, ...done].map(v => (
          <VisitCard key={v.id} visit={v} adminToken={adminToken} onChange={load}/>
        ))}
      </main>
    </div>
  );
}

function VisitCard({ visit, adminToken, onChange }) {
  const [busy, setBusy] = useState(false);
  const [gps, setGps] = useState(null);
  const [showComplete, setShowComplete] = useState(false);

  const start = async () => {
    setBusy(true);
    try {
      // Capture GPS at start
      navigator.geolocation?.getCurrentPosition(
        p => setGps({lat: p.coords.latitude, lng: p.coords.longitude}),
        () => {}, {maximumAge: 30000}
      );
      await apiCall(`/health-services/nurses/visits/${visit.id}/start`, {method:"POST"}, adminToken);
      onChange();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };

  const STATUS = { planned:"bg-[#DBEAFE] text-[#1D4ED8]",
                    in_progress:"bg-[#FEF3C7] text-[#B45309]",
                    completed:"bg-[#D4E0D1] text-[#1A3317]" };

  return (
    <div className="bg-white rounded-lg border border-[#E5E2D9] p-4" data-testid={`nurse-visit-${visit.visit_no}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-mono text-[11px] text-[#8A8780]">{visit.visit_no}</div>
          <div className="font-medium">{visit.customer_name}</div>
          <div className="text-[11px] text-[#5C5A55]">{visit.customer_phone} · {visit.planned_time}</div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[11px] ${STATUS[visit.status]||""}`}>{visit.status?.replace(/_/g," ")}</span>
      </div>
      <div className="text-sm text-[#5C5A55] flex items-start gap-1 mb-3">
        <MapPin size={14} className="mt-0.5 flex-shrink-0"/>
        <span>{visit.address}</span>
      </div>
      {visit.status === "planned" && (
        <button onClick={start} disabled={busy} data-testid={`nurse-start-${visit.visit_no}`}
                className="w-full py-2 rounded bg-[#B91C1C] text-white font-medium disabled:opacity-50">
          <Play size={14} className="inline mr-1" weight="fill"/> Start visit
        </button>
      )}
      {visit.status === "in_progress" && (
        <button onClick={() => setShowComplete(true)} data-testid={`nurse-complete-${visit.visit_no}`}
                className="w-full py-2 rounded bg-[#10B981] text-white font-medium">
          <CheckCircle size={14} className="inline mr-1"/> Complete visit
        </button>
      )}
      {visit.status === "completed" && visit.visit_note?.vitals && (
        <div className="mt-2 p-2 rounded bg-[#F5F3EE] text-[11px] text-[#5C5A55]">
          <b>Vitals:</b> {Object.entries(visit.visit_note.vitals).map(([k,v]) => `${k}: ${v}`).join(" · ")}
        </div>
      )}
      {showComplete && <CompleteModal visit={visit} adminToken={adminToken} gps={gps}
                                        onClose={() => { setShowComplete(false); onChange(); }}/>}
    </div>
  );
}

function CompleteModal({ visit, adminToken, gps, onClose }) {
  const [f, setF] = useState({bp:"", sugar:"", spo2:"", temp:"", notes:"", procedures:""});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.notes) { alert("Notes required"); return; }
    setBusy(true);
    try {
      const vitals = {};
      ["bp","sugar","spo2","temp"].forEach(k => { if (f[k]) vitals[k] = f[k]; });
      await apiCall(`/health-services/nurses/visits/${visit.id}/complete`, {
        method:"POST",
        body: JSON.stringify({
          notes: f.notes,
          vitals: Object.keys(vitals).length ? vitals : null,
          procedures_done: f.procedures ? f.procedures.split(",").map(s => s.trim()) : null,
          photo_urls: gps ? [`gps:${gps.lat.toFixed(4)},${gps.lng.toFixed(4)}`] : null,
        })
      }, adminToken);
      onClose();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}
          data-testid="nurse-complete-modal">
      <div className="bg-white rounded-lg max-w-sm w-full p-4" onClick={e => e.stopPropagation()}>
        <div className="font-heading text-lg mb-3">Complete visit</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <input placeholder="BP (120/80)" data-testid="vital-bp" value={f.bp} onChange={e => setF({...f, bp:e.target.value})} className="border rounded px-2 py-1.5 text-sm"/>
          <input placeholder="Sugar" value={f.sugar} onChange={e => setF({...f, sugar:e.target.value})} className="border rounded px-2 py-1.5 text-sm"/>
          <input placeholder="SpO₂ %" value={f.spo2} onChange={e => setF({...f, spo2:e.target.value})} className="border rounded px-2 py-1.5 text-sm"/>
          <input placeholder="Temp °F" value={f.temp} onChange={e => setF({...f, temp:e.target.value})} className="border rounded px-2 py-1.5 text-sm"/>
        </div>
        <input placeholder="Procedures (comma sep.)" value={f.procedures} onChange={e => setF({...f, procedures:e.target.value})} className="w-full border rounded px-2 py-1.5 text-sm mb-2"/>
        <textarea placeholder="Visit notes" data-testid="visit-notes" value={f.notes} onChange={e => setF({...f, notes:e.target.value})} rows={3} className="w-full border rounded px-2 py-1.5 text-sm mb-2"/>
        {gps && <div className="text-[10px] text-[#8A8780] mb-2">📍 GPS tag: {gps.lat.toFixed(4)}, {gps.lng.toFixed(4)}</div>}
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded border">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="nurse-submit-complete"
                  className="flex-1 py-2 rounded bg-[#10B981] text-white disabled:opacity-50">
            {busy ? "Saving…" : "Save & close"}
          </button>
        </div>
      </div>
    </div>
  );
}
